import React, { useEffect, useMemo, useState } from "react";

import EventMotionPlayer from "./EventMotionPlayer";
import LoadingSpinner from "./LoadingSpinner";
import Modal from "./Modal";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";
import { fmtUtc } from "../lib/format";
import {
  type BoxPhoto,
  fetchPhotos,
} from "../services/boxDetailApi";

/**
 * Episode-scoped photo modal opened from a Departures row's
 * thumbnail (Jason 2026-06-18). Fetches photos in the episode's
 * day window via the existing `/api/boxes/{scu}/photos` endpoint
 * (date-granular), then narrows to the episode_start..episode_end
 * range in-component.
 *
 * Uses the same EventMotionPlayer the per-box Photos page uses —
 * the operator can scrub frame-by-frame or hit play to watch the
 * episode as a slideshow.
 */
interface Props {
  scu: string;
  episodeId: number;
  episodeStartUtc: string;
  episodeEndUtc: string | null;
  /** Optional context shown in the modal header. */
  smartboxCode?: string | null;
  itemLabel?: string | null;
  onClose: () => void;
}

function isoDate(s: string | null): string {
  if (!s) return new Date().toISOString().slice(0, 10);
  // Accept "YYYY-MM-DDTHH:MM:SSZ" or "YYYY-MM-DD".
  return s.slice(0, 10);
}

function withinWindow(
  photo: BoxPhoto, startUtc: string, endUtc: string | null,
): boolean {
  if (!photo.timestamp) return false;
  const t = Date.parse(photo.timestamp);
  if (Number.isNaN(t)) return false;
  const s = Date.parse(startUtc);
  const e = endUtc ? Date.parse(endUtc) : s + 60_000;  // ≥ 1 min fallback
  if (Number.isNaN(s) || Number.isNaN(e)) return false;
  return t >= s && t <= e;
}

export const DepartureEpisodeModal: React.FC<Props> = ({
  scu,
  episodeId,
  episodeStartUtc,
  episodeEndUtc,
  smartboxCode,
  itemLabel,
  onClose,
}) => {
  const { token } = useAuth();
  const [allPhotos, setAllPhotos] = useState<BoxPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The backend filter is date-granular; we widen by one day on
  // each side then filter in-component so episodes that straddle
  // midnight UTC still render.
  useEffect(() => {
    if (!token) return;
    const fromDate = isoDate(episodeStartUtc);
    const toDate = isoDate(episodeEndUtc ?? episodeStartUtc);
    setLoading(true);
    setError(null);
    fetchPhotos(scu, token, {
      from: fromDate, to: toDate,
      onlyPerson: true, limit: 100, offset: 0,
    })
      .then((d) => setAllPhotos(d.photos))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [scu, token, episodeStartUtc, episodeEndUtc]);

  const episodePhotos = useMemo(
    () => allPhotos
      .filter((p) => withinWindow(p, episodeStartUtc, episodeEndUtc))
      .slice()
      .sort((a, b) => {
        const ta = a.timestamp ? Date.parse(a.timestamp) : 0;
        const tb = b.timestamp ? Date.parse(b.timestamp) : 0;
        return ta - tb;
      }),
    [allPhotos, episodeStartUtc, episodeEndUtc],
  );

  const durationSeconds = useMemo(() => {
    if (!episodeEndUtc) return null;
    const s = Date.parse(episodeStartUtc);
    const e = Date.parse(episodeEndUtc);
    if (Number.isNaN(s) || Number.isNaN(e)) return null;
    return Math.max(1, Math.round((e - s) / 1000));
  }, [episodeStartUtc, episodeEndUtc]);

  return (
    <Modal
      open
      onClose={onClose}
      widthClass="max-w-4xl"
      title={
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span>Episode #{episodeId}</span>
          {smartboxCode && (
            <span className="text-secondary-500 text-sm font-normal">
              {smartboxCode}
            </span>
          )}
          {itemLabel && (
            <span className="text-secondary-500 text-sm font-normal font-mono">
              {itemLabel}
            </span>
          )}
        </div>
      }
    >
      <div className="space-y-3">
        <div className="text-xs text-secondary-500">
          {fmtUtc(episodeStartUtc)}
          {episodeEndUtc && episodeEndUtc !== episodeStartUtc && (
            <> → {fmtUtc(episodeEndUtc)}</>
          )}
          {durationSeconds != null && <> · {durationSeconds}s</>}
          {episodePhotos.length > 0 && (
            <> · {episodePhotos.length} photo{episodePhotos.length === 1 ? "" : "s"}</>
          )}
        </div>

        {error && (
          <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">
            {error}
          </div>
        )}

        {loading ? (
          <LoadingSpinner label="loading photos" />
        ) : episodePhotos.length === 0 ? (
          <div className="text-sm text-secondary-600 bg-secondary-50 px-3 py-6 rounded text-center">
            No AI-confirmed person photos in this episode window.
            {allPhotos.length > 0 && (
              <div className="text-xs mt-1 text-secondary-500">
                ({allPhotos.length} photo{allPhotos.length === 1 ? "" : "s"} on this date didn't fall in the
                episode time range)
              </div>
            )}
          </div>
        ) : (
          <EventMotionPlayer
            photos={episodePhotos}
            durationSeconds={durationSeconds}
          />
        )}

        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DepartureEpisodeModal;
